import React from 'react';
import {
  RiCloseLine,
  RiArrowDownSLine,
  RiAttachmentLine,
  RiPlayFill,
  RiPlayLine,
  RiPauseLine,
  RiStopFill,
  RiFileTextLine,
  RiTimerLine,
} from 'react-icons/ri';

const TOOL_BTN = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--text-secondary, #888)', padding: '4px',
  display: 'flex', alignItems: 'center', borderRadius: '6px', flexShrink: 0,
  transition: 'color 0.15s',
};

const ACTION_BTN = (bg, disabled = false) => ({
  background: disabled ? 'var(--text-secondary, #aaa)' : bg,
  border: 'none', borderRadius: '8px',
  width: '32px', height: '32px',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: disabled ? 'not-allowed' : 'pointer',
  color: '#fff', flexShrink: 0, opacity: disabled ? 0.5 : 1,
  transition: 'background 0.15s',
  position: 'relative',
});

const SwarmMode = ({
  question,
  setQuestion,
  handleKeyDown,
  textareaStyle,
  files,
  fileObjectUrls,
  removeFile,
  isSwarmRunning,
  isSwarmTerminal,
  handleStartSwarm,
  handleInterveneSwarm,
  isSwarmStopping,
  swarm,
  isMobileView,
  isPauseUpdating,
  isSwarmPaused,
  handleResumeSwarm,
  handlePauseSwarm,
  isSummarizeDisabled,
  summaryInProgress,
  swarmMaxTurns,
  setSwarmMaxTurns,
  fileInputRef,
  currentPauseMode,
  isDelayUpdating,
  currentTurnDelay,
  usePriorContext,
  setUsePriorContext,
  hasPriorContextAvailable,
  onHide,
  onStop,
}) => {
  const isActive = isSwarmRunning && !isSwarmTerminal;

  /* Shared by the running and terminal branches — one definition so the
     two can't drift. Must stay type="button": the surrounding form's
     submit starts or intervenes in a swarm. */
  const summarizeButton = (
    <button type="button"
      onClick={() => swarm?.getSummary && swarm.getSummary()}
      disabled={isSummarizeDisabled}
      style={ACTION_BTN('var(--primary-color, #636cff)', isSummarizeDisabled)}
      title={summaryInProgress ? 'Summary in progress…' : 'Summarize debate'}>
      <RiFileTextLine size={15} />
      {summaryInProgress && <div className="loading-spinner" style={{ position: 'absolute', width: '8px', height: '8px', border: '2px solid rgba(255,255,255,0.3)', borderLeftColor: '#fff' }} />}
    </button>
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!isActive) { handleStartSwarm(); }
        else { handleInterveneSwarm(); }
      }}
    >
      {/* Prior-context toggle */}
      {hasPriorContextAvailable && !isActive && (
        <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            type="button" role="switch" aria-checked={usePriorContext}
            style={{
              position: 'relative', width: '44px', height: '22px',
              backgroundColor: usePriorContext ? 'var(--primary-color, #636cff)' : '#ccc',
              borderRadius: '11px', cursor: 'pointer', transition: 'background-color 0.3s ease',
              flexShrink: 0, border: 'none', padding: 0, touchAction: 'manipulation',
            }}
            onClick={() => setUsePriorContext(prev => !prev)}
          >
            <div style={{
              position: 'absolute', top: '2px',
              left: usePriorContext ? '24px' : '2px',
              width: '18px', height: '18px',
              backgroundColor: 'white', borderRadius: '50%',
              transition: 'left 0.3s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </button>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #666)' }}>
            {usePriorContext ? 'Use session context for new swarm' : 'Continue without prior context'}
          </span>
        </div>
      )}

      {/* Attached file chips */}
      {files.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
          {files.map((file, index) => (
            <div key={index} style={{
              display: 'flex', alignItems: 'center',
              backgroundColor: 'var(--bg-secondary, #f0f0f0)',
              padding: '4px 8px', borderRadius: '4px',
              fontSize: '0.9rem', border: '1px solid var(--border-color, #ddd)',
            }}>
              <span
                style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', textDecoration: 'underline', color: 'var(--primary-color, #636cff)' }}
                onClick={() => { const url = fileObjectUrls.get(index); if (url) window.open(url, '_blank'); }}
                title="Click to view file"
              >
                {file.name}
              </span>
              <button type="button" aria-label={`Remove ${file.name}`} onClick={() => removeFile(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginLeft: 4, display: 'flex', alignItems: 'center', color: 'var(--text-secondary, #666)' }}><RiCloseLine size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {/* ── Unified input box ── */}
      <div style={{
        border: '1px solid var(--input-border, rgba(255,255,255,0.15))',
        borderRadius: '6px',
        background: 'var(--input-background)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <textarea
          placeholder={isActive ? 'Inject guidance into the swarm…' : 'Type your topic for the swarm debate…'}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: isMobileView ? '0.5rem' : '0.75rem',
            background: 'transparent', border: 'none', outline: 'none',
            resize: 'none', fontSize: isMobileView ? '1rem' : '1.1rem',
            lineHeight: '1.4', minHeight: isMobileView ? '2rem' : '2.5rem',
            color: 'inherit', fontFamily: 'inherit',
          }}
          disabled={isSwarmStopping || (swarm?.starting && !swarm?.streaming)}
          required={files.length === 0}
          rows={isMobileView ? 1 : 2}
        />

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 6px 6px', gap: '6px' }}>

          {/* Left: attach + turns counter (always shown, compact on mobile) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <button
              type="button" style={TOOL_BTN}
              onClick={() => fileInputRef.current.click()}
              title="Attach file (PNG, JPG, WEBP, PDF, TXT)"
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--primary-color, #636cff)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary, #888)'; }}
            >
              <RiAttachmentLine size={18} />
            </button>

            {/* Turns — direct number input so users can type any value quickly */}
            {!isActive && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--text-secondary, #666)' }} title="Maximum turns">
                <RiTimerLine size={14} />
                {!isMobileView && <label style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>Turns</label>}
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={swarmMaxTurns}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!isNaN(v)) setSwarmMaxTurns(Math.min(500, Math.max(1, v)));
                    else setSwarmMaxTurns('');
                  }}
                  onBlur={(e) => { if (!e.target.value) setSwarmMaxTurns(12); }}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{
                    width: '52px', textAlign: 'center', fontWeight: 'bold',
                    fontSize: '0.82rem', padding: '2px 4px',
                    border: '1px solid var(--border-color, #555)',
                    borderRadius: '5px',
                    background: 'var(--input-background)',
                    color: 'var(--text-color)',
                    outline: 'none',
                  }}
                />
              </div>
            )}

          </div>

          {/* Right: steps pill + hide + action buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>


            {!isActive ? (
              /* Pre-start: Start. Terminal: Start + Summarize — the backend
                 can still summarize a stopped/completed session, so keep the
                 button reachable after the debate ends. */
              <>
                <button type="submit" disabled={isSwarmStopping}
                  style={ACTION_BTN('var(--primary-color, #636cff)', isSwarmStopping)}
                  title="Start Swarm">
                  <RiPlayFill size={15} />
                </button>
                {isSwarmTerminal && summarizeButton}
              </>
            ) : (
              /* Running: Intervene + Stop + Pause + Summarize */
              <>
                <button type="submit"
                  disabled={(!question.trim() && files.length === 0) || swarm?.intervening}
                  style={{ ...ACTION_BTN('#28a745', (!question.trim() && files.length === 0) || swarm?.intervening), position: 'relative' }}
                  title="Send guidance to the swarm">
                  <RiPlayFill size={15} />
                  {swarm?.intervening && <div className="loading-spinner" style={{ position: 'absolute', width: '8px', height: '8px', border: '2px solid rgba(255,255,255,0.3)', borderLeftColor: '#fff' }} />}
                </button>

                <button type="button" onClick={onStop} disabled={isSwarmStopping}
                  style={{ ...ACTION_BTN('var(--color-danger, #dc3545)', isSwarmStopping), position: 'relative' }}
                  title="Stop swarm">
                  <RiStopFill size={15} />
                  {isSwarmStopping && <div className="loading-spinner" style={{ position: 'absolute', width: '8px', height: '8px', border: '2px solid rgba(255,255,255,0.3)', borderLeftColor: '#fff' }} />}
                </button>

                <button type="button"
                  onClick={isSwarmPaused ? handleResumeSwarm : handlePauseSwarm}
                  disabled={isPauseUpdating}
                  style={ACTION_BTN(isSwarmPaused ? '#17a2b8' : '#6c757d', isPauseUpdating)}
                  title={isSwarmPaused ? 'Resume swarm' : 'Pause swarm'}>
                  {isSwarmPaused ? <RiPlayLine size={15} /> : <RiPauseLine size={15} />}
                </button>

                {summarizeButton}
              </>
            )}

            {/* Collapse card — always last/rightmost */}
            {onHide && (
              <button type="button" style={TOOL_BTN} onClick={onHide} title="Hide input panel"
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--primary-color, #636cff)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary, #888)'; }}>
                <RiArrowDownSLine size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Secondary controls: pause-on-turn toggle + turn delay ─────────
          Only visible while a swarm session is actively running.           */}
      {isActive && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px', marginTop: '8px', padding: '0 2px' }}>

          {/* Pause-on-turn toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              role="switch"
              aria-checked={currentPauseMode === 'per_turn'}
              disabled={isPauseUpdating}
              style={{
                position: 'relative', width: '36px', height: '18px',
                backgroundColor: currentPauseMode === 'per_turn' ? 'var(--primary-color, #636cff)' : 'var(--text-secondary, #888)',
                borderRadius: '9px', cursor: isPauseUpdating ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.25s', opacity: isPauseUpdating ? 0.6 : 1,
                border: 'none', padding: 0, flexShrink: 0,
              }}
              onClick={() => {
                if (!isPauseUpdating && swarm?.setPauseMode) {
                  swarm.setPauseMode(currentPauseMode === 'none' ? 'per_turn' : 'none');
                }
              }}
            >
              <div style={{
                position: 'absolute', top: '2px',
                left: currentPauseMode === 'per_turn' ? '20px' : '2px',
                width: '14px', height: '14px',
                backgroundColor: '#fff', borderRadius: '50%',
                transition: 'left 0.25s', boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
              }} />
            </button>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary, #888)', whiteSpace: 'nowrap' }}>
              {currentPauseMode === 'per_turn' ? 'Pause after each turn' : 'No auto-pause'}
            </span>
          </div>

          {/* Turn delay dropdown — 4 preset options; hidden when pausing per turn */}
          {currentPauseMode !== 'per_turn' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RiTimerLine size={14} style={{ color: 'var(--text-secondary, #888)', flexShrink: 0 }} />
              {!isMobileView && (
                <label style={{ fontSize: '0.78rem', color: 'var(--text-secondary, #888)', whiteSpace: 'nowrap' }}>Delay</label>
              )}
              <select
                value={currentTurnDelay}
                disabled={isDelayUpdating}
                onChange={(e) => swarm?.setTurnDelay && swarm.setTurnDelay(Number(e.target.value))}
                style={{
                  fontSize: '0.78rem', padding: '2px 20px 2px 4px',
                  border: '1px solid var(--border-color, #555)',
                  borderRadius: '5px',
                  background: 'var(--input-background)',
                  color: 'var(--text-color)',
                  cursor: isDelayUpdating ? 'not-allowed' : 'pointer',
                  opacity: isDelayUpdating ? 0.6 : 1,
                  outline: 'none',
                  // Remove the native OS arrow (often black even on dark themes)
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  // Themed chevron SVG — matches --text-secondary
                  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%23888' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E\")",
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 4px center',
                }}
              >
                {/* Show a disabled placeholder if the server has a non-preset value (e.g. 5s) */}
                {![0, 10, 30, 60].includes(currentTurnDelay) && (
                  <option value={currentTurnDelay} disabled>
                    {currentTurnDelay} s (current)
                  </option>
                )}
                <option value={0}>0 s</option>
                <option value={10}>10 s</option>
                <option value={30}>30 s</option>
                <option value={60}>60 s</option>
              </select>
            </div>
          )}
        </div>
      )}
    </form>
  );
};

export default SwarmMode;
